<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!isPublishing"
    :close-on-escape="!isPublishing"
    :dismissable-mask="!isPublishing"
    :style="{ width: '40rem', maxWidth: 'calc(100vw - 1rem)' }"
    :pt="{ root: { 'aria-labelledby': titleId } }"
    @update:visible="$emit('update:visible', $event)">
    <template #container>
      <div
        v-if="published"
        data-test="publish-success"
        class="flex flex-col items-center gap-2.5 px-8 pt-12 pb-8 text-center">
        <span class="flex size-12 items-center justify-center rounded-full bg-positive-soft text-positive">
          <CheckIcon class="size-6" aria-hidden="true" />
        </span>
        <h2 :id="titleId" class="mt-1.5 text-xl/[1.3] font-semibold text-heading">
          {{
            liveTotal === 0 ? 'Nothing went live' : `${liveTotal} ${liveTotal === 1 ? 'change is' : 'changes are'} live`
          }}
        </h2>
        <p v-if="liveTotal > 0" class="max-w-[380px] text-sm/normal text-pretty text-copy">
          Visitors on {{ liveDestination }} see them on their next page view. Your note is saved in publish history.
        </p>
        <div
          v-if="hostedIssues.length > 0"
          data-test="publish-hosted-result"
          class="mt-2 flex w-full max-w-[460px] items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3 text-left">
          <ExclamationTriangleIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <div class="flex min-w-0 flex-col gap-1.5 text-sm text-copy">
            <p class="font-semibold text-heading">
              {{ hostedIssueTitle }}
            </p>
            <ul class="flex flex-col gap-1">
              <li v-for="issue in hostedIssues" :key="issue.key" data-test="publish-hosted-issue">
                <span v-if="issue.name" class="font-medium text-heading">{{ issue.name }}: </span>{{ issue.reason }}
              </li>
            </ul>
          </div>
        </div>
        <Button
          class="mt-3 cursor-pointer"
          size="small"
          severity="secondary"
          outlined
          label="Done"
          @click="$emit('update:visible', false)" />
      </div>

      <div v-else class="flex max-h-[min(840px,calc(100dvh-80px))] min-h-0 flex-col">
        <header class="flex items-start gap-4 border-b border-line px-6 pt-[22px] pb-4">
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <h2 :id="titleId" class="text-xl/[1.3] font-semibold text-heading">
              Publish {{ total }} {{ total === 1 ? 'change' : 'changes' }}
            </h2>
            <p class="text-sm/normal text-copy">They go live on {{ reviewDestination }} as soon as you publish.</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            title="Close"
            :disabled="isPublishing"
            class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted hover:bg-overlay hover:text-heading disabled:cursor-not-allowed disabled:opacity-40"
            @click="$emit('update:visible', false)">
            <XMarkIcon class="size-[18px]" />
          </button>
        </header>

        <div class="flex min-h-0 flex-1 flex-col gap-[22px] overflow-y-auto px-6 pt-[18px] pb-[22px]">
          <div
            v-if="hostedStale"
            data-test="publish-hosted-stale"
            role="alert"
            class="flex items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3">
            <ArrowPathIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
            <div class="flex min-w-0 flex-1 flex-col gap-1 text-sm text-copy">
              <p class="font-semibold text-heading">Pages changed since you opened the review</p>
              <p>Nothing was published. Refresh the list, check it, then publish again.</p>
            </div>
            <Button
              data-test="publish-hosted-refresh"
              class="shrink-0 cursor-pointer"
              size="small"
              severity="secondary"
              outlined
              label="Refresh"
              :disabled="isPublishing"
              @click="$emit('refresh-hosted')" />
          </div>

          <div
            v-if="hostedBlock && hostedTotal > 0"
            data-test="publish-hosted-block"
            class="flex items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3">
            <LockClosedIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
            <div class="flex flex-col gap-1.5 text-sm text-copy">
              <p class="font-semibold text-heading">Your page changes won't go live</p>
              <p>{{ hostedBlock.reason }}</p>
            </div>
          </div>

          <div
            v-if="placeholderPages.length > 0"
            data-test="publish-placeholders"
            class="flex items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3">
            <ExclamationTriangleIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
            <div class="flex flex-col gap-1.5 text-sm text-copy">
              <p class="font-semibold text-heading">Some pages still have placeholders to fill</p>
              <p>Placeholders stay hidden from visitors until you fill them, so those spots won't show yet.</p>
              <ul class="flex flex-col gap-1">
                <li v-for="page in placeholderPages" :key="page.key" data-test="publish-placeholder-page">
                  <span class="font-medium text-heading">{{ page.name }}</span>
                  · {{ page.count }} {{ page.count === 1 ? 'placeholder' : 'placeholders' }}
                </li>
              </ul>
            </div>
          </div>

          <div
            v-if="orphanedPages.length > 0"
            data-test="publish-orphaned-changes"
            role="alert"
            class="flex items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3">
            <ExclamationTriangleIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
            <div class="flex min-w-0 flex-col gap-1.5 text-sm text-copy">
              <p class="font-semibold text-heading">Some campaign changes will stop applying</p>
              <p
                >These page edits remove elements that campaign changes target. Put the elements back first, or publish
                and update the campaigns.</p
              >
              <div v-for="page in orphanedPages" :key="page.key" class="flex flex-col gap-1">
                <p class="font-medium text-heading">{{ page.name }}</p>
                <ul class="flex list-disc flex-col gap-1 pl-5">
                  <li
                    v-for="orphan in page.orphans"
                    :key="`${orphan.campaignId}:${orphan.audience}:${orphan.version ?? ''}:${orphan.elementId}`"
                    data-test="publish-orphaned-change">
                    {{ orphan.message }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div
            v-if="strippedFeatures.length > 0"
            data-test="publish-stripped-features"
            class="flex items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3">
            <ExclamationTriangleIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
            <div class="flex flex-col gap-1.5 text-sm text-copy">
              <p class="font-semibold text-heading">Some features won't go live on your current plan</p>
              <ul class="flex flex-col gap-1">
                <li v-for="feature in strippedFeatures" :key="feature.key" class="flex items-center gap-2">
                  <LockClosedIcon class="size-3.5 shrink-0 text-muted" aria-hidden="true" />
                  {{ feature.label }} need {{ feature.requiredTier }}
                </li>
              </ul>
              <router-link
                :to="{ name: ROUTES.UPGRADE }"
                class="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                Upgrade <ArrowRightIcon class="size-3.5" aria-hidden="true" />
              </router-link>
            </div>
          </div>

          <div
            v-if="hasNoSubscription"
            data-test="publish-go-live-trial-callout"
            class="flex items-start gap-3 rounded-lg border border-line bg-primary-soft px-4 py-3">
            <InformationCircleIcon class="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
            <div class="flex flex-col gap-1 text-sm text-copy">
              <template v-if="projectTrialDays === null">
                <p class="font-semibold text-heading">Going live starts this project’s plan</p>
                <p>
                  Next, you’ll choose a plan and add a card. Only an organization’s first project gets a free trial, so
                  your card is charged when that succeeds and RightMessage publishes these changes right away.
                </p>
              </template>
              <template v-else>
                <p class="font-semibold text-heading">Going live starts your {{ projectTrialDays }}-day trial</p>
                <p>
                  Next, you’ll choose a plan and add a card. Your trial starts when that succeeds and RightMessage
                  publishes these changes right away. Your card isn’t charged during the trial.
                </p>
              </template>
            </div>
          </div>

          <div
            data-test="publish-change-groups"
            class="flex shrink-0 flex-col overflow-hidden rounded-[10px] border border-line bg-[var(--rm-app-card)]">
            <section
              v-for="(group, groupIndex) in groups"
              :key="group.key"
              :data-test="`publish-group-${group.key}`"
              :class="groupIndex > 0 ? 'border-t border-line' : ''">
              <button
                type="button"
                class="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left hover:bg-tint"
                :aria-expanded="openGroups.has(group.key)"
                :aria-controls="`${titleId}-${group.key}`"
                @click="toggleGroup(group.key)">
                <span class="flex size-7 shrink-0 items-center justify-center rounded-md bg-overlay text-copy">
                  <component :is="group.icon" class="size-[15px]" aria-hidden="true" />
                </span>
                <span class="text-sm/[1.3] font-semibold text-heading">{{ group.label }}</span>
                <span class="flex-1 text-[13px] text-muted">{{ group.summary }}</span>
                <ChevronDownIcon v-if="openGroups.has(group.key)" class="size-4 text-muted" aria-hidden="true" />
                <ChevronRightIcon v-else class="size-4 text-muted" aria-hidden="true" />
              </button>
              <ul
                v-if="openGroups.has(group.key)"
                :id="`${titleId}-${group.key}`"
                class="flex flex-col pr-4 pb-2 pl-14">
                <li
                  v-for="(item, itemIndex) in group.items"
                  :key="item.key"
                  data-test="publish-change-item"
                  :data-kind="item.kind"
                  class="flex flex-col gap-2 py-2"
                  :class="itemIndex > 0 ? 'border-t border-line' : ''">
                  <div class="flex items-start gap-2.5">
                    <PlusCircleIcon
                      v-if="item.kind === 'added'"
                      class="mt-0.5 size-4 shrink-0 text-positive"
                      aria-label="Added" />
                    <PencilSquareIcon
                      v-else-if="item.kind === 'edited'"
                      class="mt-0.5 size-4 shrink-0 text-primary"
                      aria-label="Edited" />
                    <MinusCircleIcon v-else class="mt-0.5 size-4 shrink-0 text-danger" aria-label="Removed" />
                    <span class="min-w-0 flex-1 text-sm/[1.45] text-pretty break-words text-heading">
                      <PhraseText :segments="item.name" />
                    </span>
                    <span
                      v-if="item.kind !== 'added'"
                      class="shrink-0 rounded-md px-2 py-1 text-xs font-bold"
                      :class="item.kind === 'edited' ? 'bg-primary-soft text-primary' : 'bg-danger-soft text-danger'">
                      {{ item.kind === 'edited' ? 'Edited' : 'Removed' }}
                    </span>
                  </div>
                  <div
                    v-if="item.fields.length > 0"
                    data-test="publish-change-fields"
                    :id="`${titleId}-${item.key}-fields`"
                    class="ml-[26px] flex min-w-0 flex-col gap-3 rounded-lg border border-line bg-panel px-3 py-2.5 text-[13px]/[1.4]">
                    <div
                      v-for="(field, fieldIndex) in expandedItems.has(item.key)
                        ? item.fields
                        : item.fields.slice(0, FIELD_ROWS)"
                      :key="fieldIndex"
                      class="grid min-w-0 gap-x-4 gap-y-1"
                      :class="
                        group.key === 'campaigns'
                          ? 'grid-cols-1'
                          : 'grid-cols-[minmax(0,1fr)_minmax(0,2fr)] max-sm:grid-cols-1'
                      ">
                      <span class="pt-0.5 break-words text-muted"><PhraseText :segments="field.field" /></span>
                      <span
                        v-if="group.key !== 'campaigns' || field.before.length > 0 || field.after.length > 0"
                        class="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span v-if="field.before.length === 0 && field.after.length === 0" class="text-muted"
                          >Changed</span
                        >
                        <template v-else>
                          <span
                            v-if="field.before.length > 0"
                            class="line-clamp-3 max-w-full rounded bg-danger-soft px-1.5 py-0.5 break-words text-copy line-through">
                            <PhraseText :segments="field.before" />
                          </span>
                          <ArrowRightIcon
                            v-if="field.before.length > 0 && field.after.length > 0"
                            class="size-3 shrink-0 text-muted"
                            aria-label="changed to" />
                          <span
                            v-if="field.after.length > 0"
                            class="line-clamp-3 max-w-full rounded bg-positive-soft px-1.5 py-0.5 font-medium break-words text-heading">
                            <PhraseText :segments="field.after" />
                          </span>
                          <span v-else class="text-muted">removed</span>
                        </template>
                      </span>
                    </div>
                    <button
                      v-if="item.fields.length > FIELD_ROWS"
                      type="button"
                      class="cursor-pointer self-start font-semibold text-primary hover:underline"
                      :aria-expanded="expandedItems.has(item.key)"
                      :aria-controls="`${titleId}-${item.key}-fields`"
                      @click="toggleItem(item.key)">
                      {{ expandedItems.has(item.key) ? 'Show fewer' : `Show ${item.fields.length - FIELD_ROWS} more` }}
                    </button>
                  </div>
                </li>
              </ul>
            </section>
          </div>

          <div
            v-if="trialRow"
            data-test="publish-trial-row"
            class="flex shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md bg-warning-soft px-3.5 py-2.5 text-sm/5 font-medium text-amber-700 ring-1 ring-warning/35 dark:text-warning">
            <ClockIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>{{ trialRow.title }}</span>
            <span class="size-[3px] shrink-0 rounded-full bg-current opacity-50" aria-hidden="true" />
            <button
              type="button"
              data-test="publish-trial-row-cta"
              class="cursor-pointer font-bold text-primary hover:underline"
              @click="billingDialog.open({ mode: 'keep', source: 'publish-row' })">
              {{ trialRow.cta }}
            </button>
            <span class="ml-auto font-normal text-copy">No charge until {{ trialRow.endDate }}</span>
          </div>

          <section
            v-if="restarts.length > 0"
            data-test="publish-restarts"
            :aria-labelledby="`${titleId}-restarts`"
            class="flex shrink-0 items-start gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3">
            <ArrowPathIcon class="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
            <div class="flex min-w-0 flex-1 flex-col gap-2 text-sm text-copy">
              <h3 :id="`${titleId}-restarts`" class="font-semibold text-heading">Restarts measurement</h3>
              <p>Publishing changes what these tests measure, so their results start over.</p>
              <ul class="flex flex-col gap-1">
                <li
                  v-for="row in restarts"
                  :key="row.experimentId"
                  data-test="publish-restart"
                  class="flex flex-wrap gap-x-2">
                  <span class="font-medium text-heading">{{ row.name }}</span>
                  <span class="text-muted">{{ row.campaignName }} · {{ row.detail }}</span>
                </li>
              </ul>
              <label
                v-if="acknowledgementRequired"
                class="flex cursor-pointer items-center gap-2 font-medium text-heading">
                <input
                  v-model="acknowledged"
                  type="checkbox"
                  data-test="publish-restarts-acknowledge"
                  class="cursor-pointer" />
                I understand these results start over
              </label>
            </div>
          </section>

          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between gap-2.5">
              <label :for="noteId" class="text-sm/[1.3] font-semibold text-heading">Publish note</label>
              <span
                v-if="noteLoading || noteFromRighty"
                data-test="publish-note-righty-badge"
                class="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 dark:text-violet-300">
                <RightyAvatar class="size-4" />
                {{ noteLoading ? 'Righty is drafting…' : 'Drafted by Righty' }}
              </span>
            </div>
            <Textarea
              :id="noteId"
              v-model="note"
              data-test="publish-note"
              rows="3"
              :disabled="noteLoading"
              :placeholder="
                noteLoading ? 'Righty is drafting a note about these changes…' : 'What does this publish do?'
              "
              class="publish-note w-full resize-y text-sm/normal" />
            <p v-if="canPreview" class="text-[12.5px] text-muted">
              Want to see these changes on your site first?
              <button
                type="button"
                data-test="publish-preview-link"
                class="cursor-pointer font-semibold text-primary hover:underline"
                @click="$emit('preview')">
                Click here</button
              >
              to preview in our debugger
            </p>
          </div>
        </div>

        <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-line px-6 py-3.5">
          <Button
            class="cursor-pointer"
            size="small"
            severity="secondary"
            outlined
            label="Cancel"
            :disabled="isPublishing"
            @click="$emit('update:visible', false)" />
          <Button
            data-test="publish-confirm"
            size="small"
            class="min-h-9 cursor-pointer px-4"
            :label="publishLabel"
            :loading="isPublishing"
            :disabled="
              isPublishing ||
              restartsLoading ||
              (acknowledgementRequired && !acknowledged) ||
              hostedStale ||
              nothingCanShip
            "
            @click="handlePublish" />
        </footer>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {
  ArrowPathIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Textarea from 'primevue/textarea'
import { computed, ref, useId, watch } from 'vue'
import PhraseText from '@dashboard/components/common/PhraseText.vue'
import RightyAvatar from '@dashboard/components/common/RightyAvatar.vue'
import { buildReviewGroups, fallbackPublishNote, publishNoteChanges } from './publishReview'
import type { ConfigChange } from '@dashboard/composables/useConfigChanges'
import { useFeatureGating } from '@dashboard/composables/useFeatureGating'
import { useTrialStatus } from '@dashboard/composables/useTrialStatus'
import { formatShortDate } from '@dashboard/utils/trialDates'
import { normalizePreviewUrl } from '@dashboard/utils/previewUrl'
import { useRightyHeadless } from '@dashboard/modules/righty/composables/useRightyHeadless'
import {
  restartNeedsAcknowledgement,
  usePublishImpact
} from '@dashboard/modules/personalization/composables/usePublishImpact'
import { ROUTES } from '@dashboard/router/routeNames'
import { useBillingDialogStore } from '@dashboard/stores/billingDialogStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useSubscriptionStore } from '@dashboard/stores/subscriptionStore'
import type { HostedPublishResult, HostedSite } from '@dashboard/modules/pages/services/hostedApi'

const props = defineProps<{
  visible: boolean
  changes: ConfigChange[]
  isPublishing?: boolean
  published?: boolean
  canPreview?: boolean
  /** Why hosted changes would be left out of this publish. */
  hostedBlock?: HostedSite['block']
  /** Publish answered that pages changed after this review opened. */
  hostedStale?: boolean
  hostedResult?: HostedPublishResult | null
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'publish', message: string): void
  (e: 'preview' | 'refresh-hosted'): void
}>()

const FIELD_ROWS = 6
const NOTE_TIMEOUT_MS = 10_000

const titleId = useId()
const noteId = useId()
const { draftPublishNote } = useRightyHeadless()
const { currentTeam, config, fullResponse } = storeToRefs(useConfigStore())
const { hasNoSubscription, projectTrialDays } = storeToRefs(useSubscriptionStore())
const { isFeatureLocked, requiredTierForFeature, tierDisplayName } = useFeatureGating()

const trial = useTrialStatus()
const billingDialog = useBillingDialogStore()
// The last days of an un-activated trial add one inline row that opens keep-mode billing.
const trialRow = computed(() => {
  const state = trial.state.value
  const endsAt = trial.endsAt.value
  if (
    (state !== 'last' && state !== 'today') ||
    trial.renewalEnabled.value ||
    !trial.canManageBilling.value ||
    !endsAt
  ) {
    return null
  }
  const days = trial.daysLeft.value ?? 0
  return {
    title: state === 'today' ? 'Your trial ends today' : `Your trial ends in ${days} ${days === 1 ? 'day' : 'days'}`,
    cta: trial.hasSavedCard.value ? 'Confirm your plan →' : 'Enter your card details →',
    endDate: formatShortDate(endsAt)
  }
})

const siteHost = computed(() => {
  const normalized = normalizePreviewUrl(currentTeam.value?.website ?? '')
  return normalized ? new URL(normalized).hostname.replace(/^www\./, '') : null
})
const siteLabel = computed(() => siteHost.value ?? 'your site')
const { restarts, loading: restartsLoading } = usePublishImpact()
const acknowledgementRequired = computed(() => restartNeedsAcknowledgement(restarts.value))
const acknowledged = ref(false)

const groups = computed(() =>
  buildReviewGroups(props.changes, config.value ?? null, fullResponse.value?.stable ?? null)
)
const total = computed(() => props.changes.length)
const publishedTotal = ref(0)
const hostedTotal = computed(() => props.changes.filter((change) => change.hosted).length)
const publishedHostedTotal = ref(0)
// A missing subscription has its own trial flow; any other gate (suspension, a billing
// conflict) leaves nothing that could ship.
const nothingCanShip = computed(
  () => !!props.hostedBlock && !hasNoSubscription.value && total.value > 0 && hostedTotal.value === total.value
)
const placeholderPages = computed(() =>
  props.changes.flatMap((change) =>
    change.hosted && change.hosted.placeholders > 0
      ? [{ key: `${change.category}:${change.id}`, name: change.name, count: change.hosted.placeholders }]
      : []
  )
)
const orphanedPages = computed(() =>
  props.changes.flatMap((change) =>
    change.hosted && change.hosted.orphanedChanges.length > 0
      ? [{ key: `${change.category}:${change.id}`, name: change.name, orphans: change.hosted.orphanedChanges }]
      : []
  )
)
const hostedIssues = computed(() => {
  const result = props.hostedResult
  if (!result || publishedHostedTotal.value === 0) {
    return []
  }
  if (result.outcome === 'blocked' || result.outcome === 'failed') {
    return [{ key: result.outcome, name: null, reason: result.reason ?? 'Publish again to retry.' }]
  }
  return result.blockedPages.map((page) => ({ key: page.pageId, name: page.name, reason: page.reason }))
})
const hostedIssueTitle = computed(() =>
  props.hostedResult?.outcome === 'failed'
    ? "Your page changes didn't publish"
    : props.hostedResult?.outcome === 'blocked'
      ? "Your page changes weren't published"
      : 'Some pages stayed as they were'
)
const hostedNotShipped = computed(() => {
  const result = props.hostedResult
  if (!result) {
    return 0
  }
  return result.outcome === 'blocked' || result.outcome === 'failed'
    ? publishedHostedTotal.value
    : Math.min(result.blockedPages.length, publishedHostedTotal.value)
})
const liveTotal = computed(() => publishedTotal.value - hostedNotShipped.value)
// Tag changes reach the website; page and site-style changes reach the hosted host (a custom domain once active).
const hostedHost = computed(
  () => props.changes.find((change) => change.hosted)?.hosted?.servingHost ?? 'your hosted pages'
)
const publishedHostedHost = ref('')
function destination(tag: boolean, pages: boolean, pagesHost: string): string {
  if (!pages) {
    return siteLabel.value
  }
  return tag && pagesHost !== siteLabel.value ? `${siteLabel.value} and ${pagesHost}` : pagesHost
}
const reviewDestination = computed(() =>
  destination(total.value > hostedTotal.value, hostedTotal.value > 0, hostedHost.value)
)
const liveDestination = computed(() =>
  destination(
    publishedTotal.value > publishedHostedTotal.value,
    publishedHostedTotal.value > hostedNotShipped.value,
    publishedHostedHost.value
  )
)
const publishLabel = computed(() =>
  hasNoSubscription.value
    ? projectTrialDays.value === null
      ? 'Choose a plan & publish'
      : 'Start trial & publish'
    : `Publish ${total.value} ${total.value === 1 ? 'change' : 'changes'}`
)

// Features the published config drops because the plan lacks them.
const strippedFeatures = computed(() => {
  const stripped: { key: string; label: string; requiredTier: string }[] = []
  if ((config.value?.campaigns ?? []).length > 0 && isFeatureLocked('campaigns')) {
    stripped.push({
      key: 'campaigns',
      label: 'Website personalization campaigns',
      requiredTier: tierDisplayName(requiredTierForFeature('campaigns'))
    })
  }
  if ((config.value?.goals ?? []).length > 0 && isFeatureLocked('goals')) {
    stripped.push({
      key: 'goals',
      label: 'Conversion goals',
      requiredTier: tierDisplayName(requiredTierForFeature('goals'))
    })
  }
  return stripped
})

const openGroups = ref(new Set<string>())
function toggleGroup(key: string) {
  if (!openGroups.value.delete(key)) {
    openGroups.value.add(key)
  }
}

const expandedItems = ref(new Set<string>())
function toggleItem(key: string) {
  if (!expandedItems.value.delete(key)) {
    expandedItems.value.add(key)
  }
}

const note = ref('')
const noteLoading = ref(false)
const noteFromRighty = ref(false)
let noteRequest = 0

async function draftNote(request: number) {
  note.value = ''
  noteFromRighty.value = false
  noteLoading.value = true
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const drafted = await Promise.race([
      draftPublishNote(publishNoteChanges(groups.value), siteHost.value),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), NOTE_TIMEOUT_MS)
      })
    ])
    if (request === noteRequest && drafted) {
      note.value = drafted
      noteFromRighty.value = true
    }
  } catch (error) {
    console.warn('Failed to draft publish note:', error)
  } finally {
    clearTimeout(timer)
    if (request === noteRequest) {
      if (!noteFromRighty.value) {
        note.value = fallbackPublishNote(groups.value)
      }
      noteLoading.value = false
    }
  }
}

function handlePublish() {
  publishedTotal.value = total.value
  publishedHostedTotal.value = hostedTotal.value
  publishedHostedHost.value = hostedHost.value
  // Publishing never waits for Righty; the readable summary stands in for the draft.
  emit('publish', noteLoading.value ? fallbackPublishNote(groups.value) : note.value)
}

watch(
  () => props.visible,
  (visible) => {
    noteRequest += 1
    noteLoading.value = false
    if (!visible) {
      return
    }
    acknowledged.value = false
    openGroups.value = new Set(groups.value[0] ? [groups.value[0].key] : [])
    expandedItems.value = new Set()
    if (props.changes.length > 0) {
      void draftNote(noteRequest)
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.p-textarea.publish-note {
  border-color: var(--rm-violet-200);
  background: linear-gradient(
    to right,
    color-mix(in srgb, var(--rm-violet-500) 6%, var(--rm-app-card)),
    color-mix(in srgb, oklch(65.6% 0.241 354.308) 4%, var(--rm-app-card))
  );
}
.dark .p-textarea.publish-note {
  border-color: color-mix(in srgb, var(--rm-violet-500) 45%, var(--rm-line));
}
</style>
